/**
 * "Can I prove it?" (contract v3): the Build provenance card (the last releases, each signed: commits, gate, signer,
 * destination, when, verified, its attestation) and the Compliance mapping card (per framework: evidenced, failing, no
 * evidence yet, not covered by Flow; the report as JSON or a printable page). Neither is a check: the overview's
 * `provenance` and `compliance` keys, computed by the server when read. The wording is "evidence for" and "helps
 * with": Flow never says a project is compliant or certified.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { CheckCircle, DownloadSimple, Printer, WarningCircle } from '@phosphor-icons/react';
import { CheckIcon, Pill, ScoreRing } from './QualityCommon';
import { complianceReport } from '../../services/QualityService';
import { PROVE_AREAS, destinationText, plural, provenanceGate, relativeTime, shortSha } from './qualityModel';
import { ASSESSMENT_AREA, assessmentGauges, assessmentTone } from './assessmentModel';

/** "Verified ✓" or "Not verified", as a pill. */
export function VerifiedMark({ verified, className = '' }) {
  return verified
    ? <Pill className={className} tone="pass"><CheckCircle aria-hidden size={13} weight="fill" />Verified ✓</Pill>
    : <Pill className={className} tone="fail"><WarningCircle aria-hidden size={13} weight="fill" />Not verified</Pill>;
}

VerifiedMark.propTypes = { className: PropTypes.string, verified: PropTypes.bool };

/** The commits of a release, short: "api a1b2c3d · web f0e1d2c". */
export function Commits({ commits = [] }) {
  if (!commits.length) { return <span className="quality-muted">No commit</span>; }
  return (
    <span className="quality-prov-commits">
      {commits.map((c, i) => (
        <React.Fragment key={`${c.service}-${c.sha}`}>
          {i > 0 && <span aria-hidden className="quality-muted"> · </span>}
          <span>{c.service} <code className="quality-mono" title={c.sha}>{shortSha(c.sha)}</code></span>
        </React.Fragment>
      ))}
    </span>
  );
}

Commits.propTypes = { commits: PropTypes.array };

/** Short form of a key id: its last 8 characters after "flow-prov-v1-". */
export const shortKeyid = (keyid) => (keyid ? String(keyid).replace(/^(flow-prov-v\d+-)(.{8}).*$/, '$1$2…') : '–');

/**
 * Opens the printable compliance report: the window opens now, while this is still a click (popup blockers), the HTML
 * is fetched with the auth headers and shown through a blob URL. Rejects (the window closed) when it cannot be fetched.
 */
export async function openPrintableReport(projectId) {
  const win = window.open('', '_blank');
  try {
    const html = await complianceReport(projectId, 'html');
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    if (win) { win.location.href = url; } else { window.open(url, '_blank'); }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    win?.close?.();
    throw e;
  }
}

export function ProvenanceCard({ provenance, onOpen, onOpenItem, onDownload }) {
  const meta = PROVE_AREAS.provenance;
  const recent = provenance?.recent || [];
  const latest = provenance?.latest || recent[0] || null;
  const devKey = provenance?.key?.source === 'dev_file';
  const tone = !latest ? 'info' : latest.verified ? 'pass' : 'fail';
  return (
    <article aria-labelledby="quality-card-title-provenance" className={`quality-card quality-check quality-prove is-${tone}${tone === 'fail' ? ' is-attention' : ''}`} data-testid="quality-card-provenance">
      <div className="quality-check-head">
        <CheckIcon path={meta.icon} />
        <h3 id="quality-card-title-provenance" title={meta.purpose}>{meta.title}</h3>
        {latest ? <VerifiedMark verified={latest.verified} /> : <Pill tone="info">No release yet</Pill>}
      </div>
      <div className="quality-check-metric">
        <div>
          <span className="quality-metric">{provenance?.total ? plural(provenance.total, 'release') : 'No release'}</span>
          <span className="quality-muted quality-small">
            {provenance?.key?.keyid ? <>signed with <code className="quality-mono" title={provenance.key.keyid}>{shortKeyid(provenance.key.keyid)}</code></> : 'signed when published'}
          </span>
        </div>
      </div>
      {devKey && <p className="quality-todo is-warn quality-small" data-testid="quality-prov-devkey">Signed with a local development key: fine for trying Flow out, not for real releases.</p>}
      {recent.length ? (
        <ul aria-label="Last releases" className="quality-prov-list">
          {recent.map((release) => {
            const gate = provenanceGate(release);
            return (
              <li data-testid={`quality-prov-${release.id}`} key={release.id}>
                <div className="quality-prov-line">
                  <button className="quality-link" onClick={() => onOpenItem(release.id)} title="See this release" type="button">{relativeTime(release.at)}</button>
                  <span className="quality-muted quality-small">{destinationText(release.destination)}</span>
                  <span className="quality-grow" />
                  <VerifiedMark className="is-xs" verified={release.verified} />
                </div>
                <div className="quality-prov-line quality-small">
                  <Commits commits={release.commits} />
                  <Pill className="is-xs" tone={gate.tone}>{gate.label}</Pill>
                  <span className="quality-muted">signer <code className="quality-mono" title={release.keyid}>{shortKeyid(release.keyid)}</code></span>
                  <span className="quality-grow" />
                  <button aria-label={`Download attestation ${release.id}`} className="quality-btn is-sm is-quiet" onClick={() => onDownload(release.id)} title="Download attestation (.intoto.jsonl)" type="button">
                    <DownloadSimple aria-hidden size={13} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="quality-muted quality-small" data-testid="quality-prov-empty">No release recorded yet: the next Publish records one.</p>
      )}
      <div className="quality-check-foot">
        <span className="quality-muted quality-small quality-grow">{latest ? `Last release ${relativeTime(latest.at)}` : 'Recorded at each Publish'}</span>
        {latest && <button className="quality-btn is-sm" onClick={() => onDownload(latest.id)} type="button"><DownloadSimple aria-hidden size={13} />Download attestation</button>}
        <button aria-label={`Open ${meta.title}`} className="quality-btn is-sm" onClick={onOpen} type="button">Open</button>
      </div>
    </article>
  );
}

ProvenanceCard.propTypes = {
  onDownload: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  onOpenItem: PropTypes.func.isRequired,
  provenance: PropTypes.object,
};

/** The counts of a framework: evidenced, failing, no evidence yet, not covered by Flow, with a thin stacked bar. */
export function FrameworkTotals({ totals = {} }) {
  const parts = [
    { key: 'evidenced', label: 'evidenced', tone: 'pass', n: totals.evidenced || 0 },
    { key: 'failing', label: 'failing', tone: 'fail', n: totals.failing || 0 },
    { key: 'noEvidence', label: 'no evidence yet', tone: 'warn', n: totals.noEvidence || 0 },
    { key: 'notCovered', label: 'not covered by Flow', tone: 'info', n: totals.notCovered || 0 },
  ];
  const all = totals.controls || parts.reduce((sum, p) => sum + p.n, 0) || 1;
  return (
    <>
      <span aria-hidden className="quality-comp-bar">
        {parts.map((p) => (p.n ? <span className={`is-${p.tone}`} key={p.key} style={{ width: `${(100 * p.n) / all}%` }} /> : null))}
      </span>
      <span className="quality-comp-counts quality-small">
        {parts.map((p) => <span data-testid={`quality-comp-${p.key}`} key={p.key}><b className={p.tone === 'fail' && p.n ? 'quality-ink-fail' : undefined}>{p.n}</b> {p.label}</span>)}
      </span>
    </>
  );
}

FrameworkTotals.propTypes = { totals: PropTypes.object };

export function ComplianceCard({ compliance, onOpen, onDownloadJson, onPrint }) {
  const meta = PROVE_AREAS.compliance;
  const frameworks = compliance?.frameworks || [];
  const failing = frameworks.some((f) => f.totals?.failing > 0);
  return (
    <article aria-labelledby="quality-card-title-compliance" className={`quality-card quality-check quality-prove is-${failing ? 'warn' : 'info'}`} data-testid="quality-card-compliance">
      <div className="quality-check-head">
        <CheckIcon path={meta.icon} />
        <h3 id="quality-card-title-compliance" title={meta.purpose}>{meta.title}</h3>
        <Pill tone={failing ? 'warn' : 'info'}>{failing ? 'Evidence failing' : 'Evidence'}</Pill>
      </div>
      <p className="quality-muted quality-small">Which controls these checks give evidence for.</p>
      <ul aria-label="Frameworks" className="quality-comp-list">
        {frameworks.map((f) => (
          <li data-testid={`quality-comp-framework-${f.id}`} key={f.id}>
            <strong>{f.name} {f.version && !String(f.name).includes(f.version) ? f.version : ''}</strong>
            <FrameworkTotals totals={f.totals} />
          </li>
        ))}
      </ul>
      {compliance?.disclaimer && <p className="quality-muted quality-small quality-comp-disclaimer" data-testid="quality-comp-disclaimer">{compliance.disclaimer}</p>}
      <div className="quality-check-foot">
        <span className="quality-muted quality-small quality-grow">Helps with an assessment: it is evidence, not an audit.</span>
        <button className="quality-btn is-sm" onClick={onDownloadJson} type="button"><DownloadSimple aria-hidden size={13} />Download report (JSON)</button>
        <button className="quality-btn is-sm" onClick={onPrint} type="button"><Printer aria-hidden size={13} />Open printable report</button>
        <button aria-label={`Open ${meta.title}`} className="quality-btn is-sm" onClick={onOpen} type="button">Open</button>
      </div>
    </article>
  );
}

ComplianceCard.propTypes = {
  compliance: PropTypes.object,
  onDownloadJson: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  onPrint: PropTypes.func.isRequired,
};

/**
 * Compliance assessments: four gauges in a row (two by two when narrow), GDPR, NIS2, DORA and the EU AI Act, each its
 * score ring (empty, "–", while it has none), its name, on or off and its gaps when known; a click opens that one.
 * `all`: every assessment, on or off (GET /quality/assessments), else only the ones on in the overview are known.
 * Evidence for an assessment, not a certification.
 */
export function AssessmentsCard({ assessments, all = null, onOpen }) {
  const frameworks = assessments?.frameworks || [];
  const gauges = assessmentGauges(frameworks, all || []);
  const gaps = frameworks.reduce((n, f) => n + (f.totals?.gap || 0), 0);
  const tone = !frameworks.length ? 'info' : gaps ? 'warn' : 'pass';
  return (
    <article aria-labelledby="quality-card-title-assessments" className={`quality-card quality-check quality-prove is-${tone}`} data-testid="quality-card-assessments">
      <div className="quality-check-head">
        <CheckIcon path={ASSESSMENT_AREA.icon} />
        <h3 id="quality-card-title-assessments" title={ASSESSMENT_AREA.purpose}>{ASSESSMENT_AREA.title}</h3>
        <Pill tone={tone}>{!frameworks.length ? 'Off' : gaps ? plural(gaps, 'gap') : 'No gap'}</Pill>
      </div>
      {!frameworks.length && (
        <p className="quality-muted quality-small" data-testid="quality-assess-off">GDPR, NIS2, DORA and the EU AI Act: what the checks already show, a few questions for your team, and what to fix. Off until you turn one on.</p>
      )}
      <ul aria-label="Assessments" className="quality-assess-gauges">
        {gauges.map((g) => {
          const note = !g.applies ? 'not applicable' : g.gaps === null ? null : plural(g.gaps, 'gap');
          return (
            <li key={g.id}>
              <button
                aria-label={`${g.name}: ${g.score ?? 'no score'}${g.score === null ? '' : ' out of 100'}, ${g.enabled ? 'on' : 'off'}${note ? `, ${note}` : ''}`}
                className={`quality-assess-gauge${g.enabled ? '' : ' is-off'}`}
                data-testid={`quality-assess-${g.id}`}
                onClick={() => onOpen(g.id)}
                title={`Open ${g.name}`}
                type="button"
              >
                <ScoreRing label={`${g.name} score`} score={g.score ?? undefined} size={52} tone={assessmentTone(g.score)} />
                <strong>{g.name}</strong>
                <span className="quality-muted quality-small">
                  {g.enabled ? 'on' : 'off'}
                  {note ? <> · <span className={g.gaps ? 'quality-ink-warn' : undefined}>{note}</span></> : null}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <div className="quality-check-foot">
        <span className="quality-muted quality-small quality-grow">Evidence for an assessment, not a certification.</span>
        <button aria-label={`Open ${ASSESSMENT_AREA.title}`} className="quality-btn is-sm" onClick={() => onOpen(null)} type="button">{frameworks.length ? 'Open' : 'Choose assessments'}</button>
      </div>
    </article>
  );
}

AssessmentsCard.propTypes = {
  // Every assessment, on or off (GET /quality/assessments `frameworks`); none: only the overview's are known.
  all: PropTypes.array,
  assessments: PropTypes.object,
  onOpen: PropTypes.func.isRequired,
};
